import debounce from "lodash/debounce.js";

type DebounceSettings = Parameters<typeof debounce>[2];

class DebounceCancelledError extends Error {
    constructor() {
        super("debounced call cancelled");
        this.name = "DebounceCancelledError";
    }
}

export const debounceWithPromise = <P extends unknown[], R>(
    func: (...args: P) => R | Promise<R>,
    wait = 500,
    options: DebounceSettings = {},
    // cancel() resolves pending promises with this value instead of rejecting;
    // use for consumers that can't handle rejections (e.g. RHF validators).
    cancelBehavior?: { resolveWith: R },
): ((...args: P) => Promise<R>) & { cancel: () => void } => {
    let currentPromise: Promise<R> | null = null;
    let resolveCurrentPromise: ((value: R) => void) | null = null;
    let rejectCurrentPromise: ((reason: unknown) => void) | null = null;

    const debouncedFn = debounce(
        async (...args: P) => {
            try {
                const result = await func(...args);

                if (resolveCurrentPromise) {
                    resolveCurrentPromise(result);
                }
            } catch (error) {
                // Propagate to callers awaiting the wrapped promise; without
                // this the promise never settles and the rejection is unhandled.
                if (rejectCurrentPromise) {
                    rejectCurrentPromise(error);
                }
            } finally {
                currentPromise = null;
                resolveCurrentPromise = null;
                rejectCurrentPromise = null;
            }
        },
        wait,
        options,
    );

    const wrappedFn = (...args: P) => {
        if (!currentPromise) {
            currentPromise = new Promise<R>((resolve, reject) => {
                resolveCurrentPromise = resolve;
                rejectCurrentPromise = reject;
            });
            // Mark the base promise handled; awaiting consumers still get
            // the rejection on their own chain.
            currentPromise.catch(() => {});
        }

        void debouncedFn(...args);

        return currentPromise;
    };

    wrappedFn.cancel = () => {
        debouncedFn.cancel();
        // Settle any pending promise — abandoning it leaves awaiters (e.g. a
        // form's isValidating flag) hanging forever.
        if (cancelBehavior && resolveCurrentPromise) {
            resolveCurrentPromise(cancelBehavior.resolveWith);
        } else if (rejectCurrentPromise) {
            rejectCurrentPromise(new DebounceCancelledError());
        }
        currentPromise = null;
        resolveCurrentPromise = null;
        rejectCurrentPromise = null;
    };

    return wrappedFn;
};
